"use client";
import ProductGrid from "./ProductGrid";
import { useEffect, useRef } from "react";
import ShopFilter from "./ShopFilter";
import Sorting from "./Sorting";
import { usePWA } from "@/utlis/usePWA";
import { useIsMobile } from "@/utlis/useIsMobile";
import { useT } from "@/context/I18nContext";

export default function ShopFullwidth({
	productsData = [],
	currentPage = 1,
	totalPages = 1,
	totalCount = 0,
	loading = false,
	loadingMore = false,
	onLoadMore,
	onFilterChange,
}) {
	const isPWA = usePWA();
	const isMobile = useIsMobile();
	const { t } = useT();
	const sentinelRef = useRef(null);

	useEffect(() => {
		if (!onLoadMore) return;
		const el = sentinelRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) onLoadMore();
			},
			{ rootMargin: "600px" },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [onLoadMore, totalPages, currentPage]);

	const handleSortChange = (sortValue) => {
		onFilterChange?.({ sort: sortValue });
	};

	const handleFilterChange = ({ minPrice, maxPrice, availability, onlyDiscounted }) => {
		onFilterChange?.({ minPrice, maxPrice, availability, onlyDiscounted });
	};

	return (
		<>
			<section
				className='flat-spacing-2'
				style={
					isPWA || isMobile
						? undefined
						: { marginLeft: "50px", marginRight: "50px" }
				}>
				<div className='container-full'>
					<h2 className='text-center' style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
						{t("shop_products_heading")}
					</h2>
					<div className='tf-shop-control grid-2 align-items-left'>
						<div className='tf-control-filter'>
							<a href='#filterShop' data-bs-toggle='offcanvas' className='tf-btn-filter'>
								<span className='icon icon-filter' />
								<span className='text'>{t("shop_filter")}</span>
							</a>
						</div>
						<div className='tf-control-sorting d-flex justify-content-end'>
							<div className='tf-dropdown-sort'>
								<Sorting onSortChange={handleSortChange} />
							</div>
						</div>
					</div>

					<div className='wrapper-control-shop'>
						<div className='meta-filter-shop' />

						{/* Skeleton în loc de pagină goală */}
						{loading ? (
							<ProductGridSkeleton count={32} isCompact={isPWA || isMobile} />
						) : (
							<ProductGrid allproducts={productsData} gridItems={32} />
						)}

						{!loading && (
							<>
								{currentPage < totalPages && <div ref={sentinelRef} style={{ height: 1 }} />}
								{loadingMore && (
									<div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
										<div className='shop-loadmore-spinner' />
										<style jsx>{`
											.shop-loadmore-spinner {
												width: 28px;
												height: 28px;
												border-radius: 50%;
												border: 3px solid #e5e5e5;
												border-top-color: var(--primary);
												animation: shop-spin 0.7s linear infinite;
											}
											@keyframes shop-spin {
												to {
													transform: rotate(360deg);
												}
											}
										`}</style>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</section>

			<ShopFilter onFilterChange={handleFilterChange} />
		</>
	);
}

// Skeleton simplu — păstrează layout-ul în timp ce se încarcă
function ProductGridSkeleton({ count = 32, isCompact = false }) {
	return (
		<div
			className='tf-grid-layout wrapper-shop pr_list'
			style={
				isCompact
					? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }
					: {
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
							gap: "24px",
					  }
			}>
			{Array.from({ length: count }).map((_, i) => (
				<div
					key={i}
					style={{
						height: "320px",
						borderRadius: "8px",
						background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
						backgroundSize: "200% 100%",
						animation: "shimmer 1.5s infinite",
					}}
				/>
			))}
			<style>{`
                @keyframes shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
		</div>
	);
}
